/***********************************************************************
 An instance of this class handles multiple sco's and act to the LMS
 as a single SCO with the given Scorm 1.2 API.
 The stored data will be saved compresses and encrypted in the
 suspend data field on LMS.
 ***********************************************************************/
import {SingleScoData} from "scorm/scorm_1_2/SingleScoData";
import {ScoData} from "scorm/scorm_1_2/ScoData";

import {ScormApiBase} from "scorm/common/ScormApiBase";
import {ExternalScormApiWrapper} from "scorm/common/ExternalScormApiWrapper";
import {ScoRegistry} from "scorm/common/ScoRegistry";

import {LoggingService} from "logging/LoggingService";
import {Assert} from "common/Assert";

export function ScormCourse(lmsScormApi, defaultMasteryScore = 80 ){

    Assert.checkArgument( lmsScormApi );

    const lmsApi = new ExternalScormApiWrapper( lmsScormApi, {
            initialize: "LMSInitialize",
            terminate: "LMSFinish",
            getValue: "LMSGetValue",
            setValue: "LMSSetValue",
            commit: "LMSCommit",
            getLastError: "LMSGetLastError",
            getErrorString: "LMSGetErrorString",
            getDiagnostic: "LMSGetDiagnostic"
        }),
        base = new ScormApiBase( defaultMasteryScore ),
        courseScoData = new SingleScoData(),
        scoRegistry = new ScoRegistry( ScoData );

    function refreshCourseStatus() {
        courseScoData.refreshStatus( scoRegistry.getStatus(), scoRegistry.collectSuspendData(), base.getLapsedTime() );
    }

    function getLmsMasteryScore(){

        const masteryScore = lmsApi.getValue("cmi.student_data.mastery_score");
        if ((masteryScore !== "") && !isNaN(Number(masteryScore))) {
            return masteryScore.toString();
        }

        return defaultMasteryScore;
    }

    function initializeLmsApi(){
        const lmsResult = lmsApi.initialize();
        return (lmsResult === "true") ? lmsResult : null;
    }

    /***********************************************************************
     * initializes the course by getting initial values from lms.
     * @return {String} true / false
     ***********************************************************************/
    function initialize() {

        let lmsResult = initializeLmsApi();
        if( !lmsResult ) {
            return "false";
        }

        // read only lms values -------------------------------------------------------
        courseScoData.set( "cmi.student_data.mastery_score", getLmsMasteryScore() );

        const coreChildrenCSV = lmsApi.getValue("cmi.core._children");
        if (coreChildrenCSV.indexOf("credit") !== -1) {
            courseScoData.set( "cmi.core.credit", lmsApi.getValue("cmi.core.credit") );
        }

        // read and write --------------------------------------------------
        if (coreChildrenCSV.indexOf("lesson_location") !== -1) {
            courseScoData.set( "cmi.core.lesson_location", lmsApi.getValue("cmi.core.lesson_location") );
        }

        if (coreChildrenCSV.indexOf("lesson_status") !== -1) {
            courseScoData.set( "cmi.core.lesson_status", lmsApi.getValue("cmi.core.lesson_status") );
            if( courseScoData.isNotAttempted() ) {
                courseScoData.set( "cmi.core.lesson_status", "incomplete" );
                lmsApi.setValue("cmi.core.lesson_status", "incomplete" );
            }
        }

        if (coreChildrenCSV.indexOf("score") !== -1) {
            const scoreChildrenCSV = lmsApi.getValue("cmi.core.score._children");
            if (scoreChildrenCSV.indexOf("raw") !== -1) {
               courseScoData.set( "cmi.core.score.raw", lmsApi.getValue("cmi.core.score.raw") );
            }
        }
        const encryptedSuspendData = lmsApi.getValue("cmi.suspend_data");
        courseScoData.set( "cmi.suspend_data", encryptedSuspendData );
        scoRegistry.initialize( encryptedSuspendData );

        return "true";
    }

    /***********************************************************************
     * gets the course mastery score
     * @return {Number} mastery score
     ***********************************************************************/
    function getCourseMasteryScore() {
        return Number( courseScoData.get("cmi.student_data.mastery_score") );
    }

    /***********************************************************************
     * gets the completion threshold. What is the mastery score for scorm 1.2.
     * @return {Number} mastery score
     ***********************************************************************/
    function getCompletionThreshold() {
        return Number( courseScoData.get("cmi.student_data.mastery_score") );
    }

    /***********************************************************************
     * calculates the current course score and status.
     * the refreshed values will be commit to the lms.
     * @return {String} true / false
     ***********************************************************************/
    function commitCourseStatus() {

        refreshCourseStatus();

        base.commitSuspendData( lmsApi, courseScoData, () => {
            LoggingService.error( "You are connecteted to a scorm 1.2 LMS and the suspend_data is longer than 4096 bytes!" );
        });

        courseScoData.forEachLmsRelevantValue( (value, dataModelId) => {
            const stringValue = value ? value.toString() : "";
            lmsApi.setValue( dataModelId, stringValue );
        });

        lmsApi.setValue("cmi.core.session_time", base.getLapsedTime());

        return lmsApi.commit();
    }

    /***********************************************************************
     * sets the list for the scoring relevant scos
     * @param {String} seqenceIdList comma sparated value list of ids
     * @return {String} true
     ***********************************************************************/
    function setScoreList(seqenceIdList) {
        scoRegistry.setRelevantScoList( seqenceIdList );
        return "true";
    }

    /***********************************************************************
     * gets a value for lesson sco
     * @param {String} scoId the id of the sco. can be null to get a value from the lms
     * @param {String} dataModelId scorm data model id for the the value
     * @return {String} value or an empty string, if the dataModelId is not defined
     ***********************************************************************/
    function getValue(scoId, dataModelId) {

        if( scoId === null ) {
            return lmsApi.getValue(dataModelId);
        }

        if( scoRegistry.contains( scoId ) ) {
            return scoRegistry.getValue( scoId, dataModelId );
        }

        return "";
    }

    /***********************************************************************
     * gets a value for the course
     * @param {String} dataModelId scorm data model id for the the value
     * @return {String} value or an empty string, if the does not exist
     ***********************************************************************/
    function getCourseValue( dataModelId ) {

        Assert.checkArgument( dataModelId );

        if( !courseScoData.isValid( dataModelId ) ) {
            return "false";
        }

        return courseScoData.get( dataModelId );
    }

    // revealing public api
    return {

        initialize: initialize,
        getMasteryScore: getCourseMasteryScore,
        getCompletionThreshold: getCompletionThreshold,
        commit: commitCourseStatus,
        setScoreList: setScoreList,
        getValue: getValue,
        getCourseValue: getCourseValue,

        terminate: function(){
            return lmsApi.terminate();
        },

        initializeSco: function( scoId ){
            if( !scoRegistry.contains(scoId) ) {
                scoRegistry.initializeNewSco( scoId );
                scoRegistry.setValue( scoId, "lesson_id", scoId );
            }

            return scoRegistry.get( scoId );
        },

        getCreApi: function(){
            return {
                getValue: function( dataModelId ){
                    if( courseScoData.isCourseManagedValue(dataModelId) ){
                        return courseScoData.get( dataModelId );
                    }
                    return lmsApi.getValue( dataModelId );
                },
                setValue: lmsApi.setValue,
                commit: commitCourseStatus,
                getLastError: lmsApi.getLastError,
                getErrorString: lmsApi.getErrorString,
                getDiagnostic: lmsApi.getDiagnostic,

                // for publisher cre
                refreshCourseStatus: refreshCourseStatus
            }
        }
    };
}
